/**
 * BillingInputPanel — 149-bd
 *
 * 3-tab (By Invoice / By % Kontrak / By Net Received) dgn 3 kolom sejajar
 * (DPP / PPN X% / Total Invoice). Kolom terakhir diedit → "Nilai Acuan"
 * (authoritative). Backend engine `/api/billing/calculate` adalah SATU sumber
 * kebenaran — komponen ini TIDAK menulis rumus sendiri.
 *
 * Design: sharp edges, palet ENSINERGI (Navy #0B1D3A + Orange #FF5A1F),
 * badge biru untuk DPP/PPN/Invoice, oranye retensi, hijau received net,
 * merah overflow.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------
function fmtRp(n) {
  if (n == null || n === "" || isNaN(Number(n))) return "";
  return new Intl.NumberFormat("id-ID").format(Math.round(Number(n)));
}
function parseRp(s) {
  if (s == null || s === "") return null;
  const digits = String(s).replace(/[^\d-]/g, "");
  if (!digits) return null;
  return parseInt(digits, 10);
}
function toDec(x) {
  if (x == null || x === "") return 0;
  return typeof x === "string" ? parseFloat(x) : x;
}

function useDebouncedCallback(cb, delay = 200) {
  const timer = useRef(null);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => cb(...args), delay);
  }, [cb, delay]);
}

// ---------------------------------------------------------------------------
// RupiahField — controlled input, format-on-blur, no interference typing
// ---------------------------------------------------------------------------
function RupiahField({ value, onChange, onFocus, onCommit, testid, disabled, className = "", placeholder = "0" }) {
  const [raw, setRaw] = useState("");
  const [focused, setFocused] = useState(false);
  // Sync from parent when not focused (avoid interfering with typing)
  useEffect(() => {
    if (!focused) setRaw(value == null || value === "" ? "" : fmtRp(value));
  }, [value, focused]);

  return (
    <input
      type="text"
      inputMode="numeric"
      data-testid={testid}
      disabled={disabled}
      value={raw}
      placeholder={placeholder}
      onFocus={(e) => {
        setFocused(true);
        setRaw(value == null || value === "" ? "" : String(parseRp(value) ?? ""));
        onFocus?.();
      }}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^\d]/g, "");
        setRaw(digits);
        onChange?.(digits === "" ? null : parseInt(digits, 10));
      }}
      onBlur={() => {
        setFocused(false);
        const n = parseRp(raw);
        setRaw(n == null ? "" : fmtRp(n));
        onCommit?.(n);
      }}
      className={`rounded-none border border-slate-300 px-2 py-1.5 text-sm tabular-nums focus:border-[#FF5A1F] focus:outline-none ${disabled ? "bg-slate-100 text-slate-500" : "bg-white text-[#0B1D3A]"} ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// ThreeColumn — DPP / PPN / Total Invoice (used across all 3 tabs)
// ---------------------------------------------------------------------------
function ThreeColumn({ dpp, vat, gross, refField, vatRate, onEdit, disabled }) {
  const vatPctLabel = vatRate > 0 ? `PPN ${(vatRate * 100).toFixed(vatRate * 100 === Math.floor(vatRate * 100) ? 0 : 2)}%` : "PPN (Non-PPN)";
  const badge = (field) => refField === field ? (
    <span className="text-[9px] uppercase tracking-wider text-[#FF5A1F] font-bold" data-testid={`billing-ref-${field}`}>· Nilai Acuan</span>
  ) : null;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="billing-three-col">
      <div>
        <div className="flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A]">DPP</label>
          {badge("dpp")}
        </div>
        <RupiahField testid="billing-dpp" value={dpp} onFocus={() => onEdit("dpp")} onChange={(v) => onEdit("dpp", v)} onCommit={(v) => onEdit("dpp", v, true)} disabled={disabled} className={refField === "dpp" ? "border-[#FF5A1F] border-2" : ""}/>
        <div className="text-[9px] text-slate-500">Sebelum PPN</div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-sky-800">{vatPctLabel}</label>
          {badge("vat")}
        </div>
        <RupiahField testid="billing-vat" value={vat} onFocus={() => onEdit("vat")} onChange={(v) => onEdit("vat", v)} onCommit={(v) => onEdit("vat", v, true)} disabled={disabled || vatRate === 0} className={refField === "vat" ? "border-[#FF5A1F] border-2" : ""}/>
        <div className="text-[9px] text-slate-500">Nilai PPN</div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A]">Total Invoice</label>
          {badge("gross_invoice")}
        </div>
        <RupiahField testid="billing-gross" value={gross} onFocus={() => onEdit("gross_invoice")} onChange={(v) => onEdit("gross_invoice", v)} onCommit={(v) => onEdit("gross_invoice", v, true)} disabled={disabled} className={refField === "gross_invoice" ? "border-[#FF5A1F] border-2" : ""}/>
        <div className="text-[9px] text-slate-500">DPP + PPN (termasuk PPN)</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview strip (ordered per spec)
// ---------------------------------------------------------------------------
function PreviewStrip({ res, vatRate, incomeTaxRate, incomeTaxBase, retentionRate, retentionBase, contractDpp, contractGross, totalDppInvoicedPrev }) {
  if (!res) return null;
  const pctThis = res.percentage_this_billing ? Number(res.percentage_this_billing) : null;
  // Cumulative percentage = (totalDppInvoicedPrev + this DPP) / contractDpp × 100
  const pctCumul = contractDpp > 0
    ? ((Number(totalDppInvoicedPrev || 0) + Number(res.dpp_amount || 0)) / Number(contractDpp)) * 100
    : null;
  const sisaDpp = contractDpp ? Number(contractDpp) - Number(totalDppInvoicedPrev || 0) - Number(res.dpp_amount || 0) : null;
  const sisaGross = contractGross ? Number(contractGross) - (Number(totalDppInvoicedPrev || 0) * (1 + Number(vatRate))) - Number(res.gross_invoice_amount || 0) : null;
  const over = (sisaDpp != null && sisaDpp < 0) || (sisaGross != null && sisaGross < 0);
  const cell = (label, val, tone) => (
    <div className={`px-2 py-1.5 border-r border-slate-200 ${tone}`}>
      <div className="overline text-[9px]">{label}</div>
      <div className="tabular-nums font-semibold">{typeof val === "string" ? val : formatIDR(val)}</div>
    </div>
  );
  return (
    <div className="border border-slate-200 bg-slate-50" data-testid="billing-preview">
      <div className="px-2 py-1.5 border-b border-slate-200 bg-slate-100 overline text-slate-700 text-[9px]">Preview Calculation</div>
      <div className="grid grid-cols-4">
        {cell("DPP Invoice", res.dpp_amount, "bg-white text-[#0B1D3A]")}
        {cell(`PPN ${(vatRate * 100).toFixed(vatRate * 100 === Math.floor(vatRate * 100) ? 0 : 2)}%`, res.vat_amount, "bg-sky-50 text-sky-800")}
        {cell("Total Invoice", res.gross_invoice_amount, "bg-white text-[#0B1D3A]")}
        {cell(`PPh ${(incomeTaxRate * 100).toFixed(2)}% (${incomeTaxBase.toUpperCase()})`, `− ${formatIDR(res.income_tax_amount)}`, "bg-sky-50 text-sky-800")}
      </div>
      <div className="grid grid-cols-4">
        {cell(`Retensi ${(retentionRate * 100).toFixed(2)}% (${retentionBase.toUpperCase()})`, `− ${formatIDR(res.retention_amount)}`, "bg-amber-50 text-amber-800")}
        {cell("Received Net", res.net_received_amount, "bg-emerald-50 text-emerald-800")}
        {cell("Persentase Tagihan Ini", pctThis != null ? `${pctThis.toFixed(2)}%` : "-", "bg-white text-slate-700")}
        {cell("Persentase Kumulatif", pctCumul != null ? `${pctCumul.toFixed(2)}%` : "-", pctCumul != null && pctCumul > 100 ? "bg-rose-50 text-rose-700" : "bg-white text-slate-700")}
      </div>
      <div className="grid grid-cols-2">
        <div className={`px-2 py-1.5 border-r border-slate-200 border-t border-slate-200 ${over && sisaDpp < 0 ? "bg-rose-50 text-rose-700" : "bg-white text-[#0B1D3A]"}`}>
          <div className="overline text-[9px]">Sisa DPP Kontrak</div>
          <div className="tabular-nums font-semibold" data-testid="billing-sisa-dpp">{sisaDpp != null ? formatIDR(sisaDpp) : "-"}</div>
        </div>
        <div className={`px-2 py-1.5 border-t border-slate-200 ${over && sisaGross < 0 ? "bg-rose-50 text-rose-700" : "bg-white text-[#0B1D3A]"}`}>
          <div className="overline text-[9px]">Sisa Kontrak termasuk PPN</div>
          <div className="tabular-nums font-semibold" data-testid="billing-sisa-gross">{sisaGross != null ? formatIDR(sisaGross) : "-"}</div>
        </div>
      </div>
      {res.adjustments && (res.adjustments.vat_from_rounding || res.adjustments.dpp_from_rounding || res.adjustments.net_diff) && (
        <div className="px-2 py-1.5 border-t border-amber-300 bg-amber-50 text-[10px] text-amber-800 flex items-start gap-1" data-testid="billing-rounding-note">
          <Info size={11} className="mt-0.5 shrink-0"/>
          <span>Perhitungan kolom lain disesuaikan berdasarkan nilai yang Anda input (pembulatan Rupiah{res.adjustments.net_diff ? `, selisih Net Rp ${res.adjustments.net_diff}` : ""}).</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function BillingInputPanel({
  vatRate = 0.11, incomeTaxRate = 0, incomeTaxBase = "dpp",
  retentionRate = 0, retentionBase = "gross",
  contractDpp = 0, contractGross = 0,
  totalDppInvoicedPrev = 0, totalGrossInvoicedPrev = 0,
  prevInvoiceTotal = 0,
  onResult, disabled = false, testid = "billing-input-panel",
  hideNetReceivedTab = false,  // 149-cw-r113: sembunyikan tab "By Net Received" saat mode invoice-baru
}) {
  const [tab, setTab] = useState("by_invoice");   // by_invoice | by_pct | by_net
  const [refField, setRefField] = useState("dpp"); // last-touched field
  const [dpp, setDpp] = useState(null);
  const [vat, setVat] = useState(null);
  const [gross, setGross] = useState(null);
  const [pct, setPct] = useState("");
  const [netTarget, setNetTarget] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reset tampilan saat pergantian tab — hindari nilai salah bocor lintas tab
  useEffect(() => {
    setResult(null);
    setError(null);
    setDpp(null); setVat(null); setGross(null); setPct(""); setNetTarget(null);
    setRefField(tab === "by_pct" ? "percentage" : tab === "by_net" ? "net" : "dpp");
  }, [tab]);

  // Auto-apply engine result to non-reference fields
  const applyResult = useCallback((r, source) => {
    if (!r) return;
    if (source !== "dpp") setDpp(r.dpp_amount);
    if (source !== "vat") setVat(r.vat_amount);
    if (source !== "gross_invoice") setGross(r.gross_invoice_amount);
    if (source === "percentage" || source === "net") {
      setDpp(r.dpp_amount); setVat(r.vat_amount); setGross(r.gross_invoice_amount);
    }
    setResult(r);
    onResult?.(r);
  }, [onResult]);

  const runCalc = useCallback(async (payload, source) => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/billing/calculate", payload);
      applyResult(data, source);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal kalkulasi");
    } finally { setBusy(false); }
  }, [applyResult]);

  const debouncedCalc = useDebouncedCallback(runCalc, 150);

  // Kirim payload umum untuk deductions
  const commonPayload = useMemo(() => ({
    vat_rate: vatRate,
    income_tax_rate: incomeTaxRate,
    income_tax_base: incomeTaxBase,
    retention_rate: retentionRate,
    retention_base: retentionBase,
    contract_dpp: contractDpp || null,
    contract_gross: contractGross || null,
  }), [vatRate, incomeTaxRate, incomeTaxBase, retentionRate, retentionBase, contractDpp, contractGross]);

  // Handler when a 3-col field is edited (by_invoice + by_pct + by_net tabs share this)
  const handleThreeColEdit = useCallback((field, value, commit = false) => {
    setRefField(field);
    if (field === "dpp") { setDpp(value); }
    if (field === "vat") { setVat(value); }
    if (field === "gross_invoice") { setGross(value); }
    // For PPN 0% + source=vat → block per spec
    if (field === "vat" && vatRate === 0) {
      setError("Input via PPN tidak tersedia untuk kontrak Non-PPN");
      return;
    }
    if (value == null || value <= 0) { setResult(null); return; }
    debouncedCalc({
      method: "by_invoice",
      source_field: field,
      amount: value,
      ...commonPayload,
    }, field);
  }, [vatRate, commonPayload, debouncedCalc]);

  // Handler for % tab
  const handlePctChange = useCallback((v) => {
    setPct(v); setRefField("percentage");
    const n = parseFloat(v);
    if (isNaN(n) || n <= 0 || !contractDpp) { setResult(null); return; }
    debouncedCalc({
      method: "by_pct",
      percentage: n,
      ...commonPayload,
    }, "percentage");
  }, [contractDpp, commonPayload, debouncedCalc]);

  // Reverse: recompute % when DPP/VAT/Gross change (only in by_pct tab)
  const reversePctFromDpp = useMemo(() => {
    if (tab !== "by_pct" || !result || !contractDpp) return null;
    const p = (Number(result.dpp_amount) / Number(contractDpp)) * 100;
    return isFinite(p) ? p : null;
  }, [tab, result, contractDpp]);
  useEffect(() => {
    if (tab === "by_pct" && refField !== "percentage" && reversePctFromDpp != null) {
      setPct(reversePctFromDpp.toFixed(4));
    }
  }, [tab, refField, reversePctFromDpp]);

  // Handler for Net tab
  const handleNetChange = useCallback((v, commit = false) => {
    setNetTarget(v); setRefField("net");
    if (v == null || v <= 0) { setResult(null); return; }
    debouncedCalc({
      method: "by_net",
      target_net_received: v,
      ...commonPayload,
    }, "net");
  }, [commonPayload, debouncedCalc]);

  return (
    <div className="space-y-2" data-testid={testid}>
      {/* Tab switcher — retained ENSINERGI navy style */}
      <div className="flex gap-0 border border-slate-300" data-testid="billing-tabs">
        {[
          { v: "by_invoice", label: "By Invoice" },
          { v: "by_pct", label: "By % Kontrak" },
          ...(hideNetReceivedTab ? [] : [{ v: "by_net", label: "By Net Received" }]),
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            data-testid={`billing-tab-${t.v}`}
            disabled={disabled || (t.v === "by_pct" && !contractDpp)}
            onClick={() => setTab(t.v)}
            className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold disabled:opacity-40 ${
              tab === t.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* By Invoice */}
      {tab === "by_invoice" && (
        <div className="space-y-2" data-testid="billing-body-by_invoice">
          <ThreeColumn dpp={dpp} vat={vat} gross={gross} refField={refField} vatRate={vatRate} onEdit={handleThreeColEdit} disabled={disabled}/>
          <div className="grid grid-cols-3 gap-2 text-[10px] tabular-nums">
            <div><div className="overline text-slate-500">Sisa DPP Kontrak</div><div className="text-[#0B1D3A] font-semibold">{formatIDR(contractDpp - Number(totalDppInvoicedPrev || 0))}</div></div>
            <div><div className="overline text-slate-500">Sisa Kontrak (incl PPN)</div><div className="text-[#0B1D3A] font-semibold">{formatIDR(contractGross - Number(totalGrossInvoicedPrev || 0))}</div></div>
            <div><div className="overline text-slate-500">Total Invoice Sebelumnya</div><div className="text-[#0B1D3A] font-semibold">{formatIDR(prevInvoiceTotal)}</div></div>
          </div>
        </div>
      )}

      {/* By % Kontrak */}
      {tab === "by_pct" && (
        <div className="space-y-2" data-testid="billing-body-by_pct">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A]">Persentase Tagihan Ini</label>
            <div className="relative max-w-[220px]">
              <input
                type="number" step="0.01" min="0" max="100"
                data-testid="billing-pct"
                disabled={disabled || !contractDpp}
                value={pct}
                onFocus={() => setRefField("percentage")}
                onChange={(e) => handlePctChange(e.target.value)}
                placeholder="0"
                className={`rounded-none border border-slate-300 px-2 py-1.5 text-sm tabular-nums w-full pr-8 focus:border-[#FF5A1F] focus:outline-none ${refField === "percentage" ? "border-[#FF5A1F] border-2" : ""}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
            </div>
            <div className="text-[9px] text-slate-500 mt-1">% terhadap DPP Kontrak (bukan kumulatif).</div>
          </div>
          <ThreeColumn dpp={dpp} vat={vat} gross={gross} refField={refField} vatRate={vatRate} onEdit={handleThreeColEdit} disabled={disabled || !contractDpp}/>
          <div className="text-[10px] text-slate-500">
            Jika DPP/PPN/Total Invoice diubah, "Persentase Tagihan Ini" ikut recalc.
          </div>
        </div>
      )}

      {/* By Net Received */}
      {tab === "by_net" && (
        <div className="space-y-2" data-testid="billing-body-by_net">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Net Received Target</label>
            <RupiahField testid="billing-net-target" value={netTarget} onFocus={() => setRefField("net")} onChange={(v) => handleNetChange(v)} onCommit={(v) => handleNetChange(v, true)} disabled={disabled} className={refField === "net" ? "border-[#FF5A1F] border-2 max-w-xs" : "max-w-xs"}/>
          </div>
          <ThreeColumn dpp={dpp} vat={vat} gross={gross} refField={refField} vatRate={vatRate} onEdit={handleThreeColEdit} disabled={disabled}/>
          {result && (
            <div className="grid grid-cols-2 gap-2 text-[10px] tabular-nums">
              <div><div className="overline text-emerald-700">Net Received Hasil</div><div className="text-emerald-800 font-semibold" data-testid="billing-net-result">{formatIDR(result.net_received_amount)}</div></div>
              <div><div className="overline text-slate-500">Selisih Pembulatan</div><div className={`font-semibold ${result.adjustments?.net_diff ? "text-amber-700" : "text-emerald-700"}`} data-testid="billing-net-diff">{result.adjustments?.net_diff != null ? formatIDR(result.adjustments.net_diff) : "0"}</div></div>
            </div>
          )}
          {result && (
            <div className="grid grid-cols-2 gap-2 text-[10px] tabular-nums">
              <div><div className="overline text-sky-700">PPh</div><div className="text-sky-800 font-semibold">{formatIDR(result.income_tax_amount)}</div></div>
              <div><div className="overline text-amber-700">Retensi</div><div className="text-amber-800 font-semibold">{formatIDR(result.retention_amount)}</div></div>
            </div>
          )}
        </div>
      )}

      {error && <div className="text-[11px] text-rose-700 border border-rose-300 bg-rose-50 px-2 py-1" data-testid="billing-error">{error}</div>}
      {busy && <div className="text-[9px] text-slate-400 italic">Menghitung…</div>}

      <PreviewStrip
        res={result}
        vatRate={vatRate} incomeTaxRate={incomeTaxRate} incomeTaxBase={incomeTaxBase}
        retentionRate={retentionRate} retentionBase={retentionBase}
        contractDpp={contractDpp} contractGross={contractGross}
        totalDppInvoicedPrev={totalDppInvoicedPrev}
      />
    </div>
  );
}
